import React, { FC, useEffect, useRef, useState } from 'react';
import { useUser } from 'store/User/Context';
import styled, { createGlobalStyle } from 'styled-components';
import { MapPinDto, MarketOfferTableRowDto } from 'utils/types/AiPredictorModels';

import { convertToThumbnailPath } from 'components/functions/imagesFunctions';

const PIN_LABEL_COUNT = 3;

const PinStyles = createGlobalStyle`
    .pmap-pin {
        cursor: pointer;
    }
    .pmap-pin--label {
        display: inline-flex;
        align-items: center;
        color: #fff;
        padding: 5px 10px;
        border-radius: 12px;
        font-size: 11px;
        font-weight: 700;
        white-space: nowrap;
        background: var(--color-primary, #2d3436);
        box-shadow: 0 0 0 2px #fff, 0 2px 6px rgba(0,0,0,0.35);
        z-index: 1;
    }
    .pmap-pin--label::after {
        content: '';
        position: absolute;
        bottom: -6px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-top: 6px solid var(--color-primary, #2d3436);
    }
    .pmap-pin--dot {
        display: block;
        width: 22px;
        height: 24px;
        z-index: 1;
    }
    .pmap-pin--dot svg {
        display: block;
        filter: drop-shadow(0 2px 4px rgba(0,0,0,0.35));
    }
    .pmap-pin--subject {
        display: inline-flex;
        align-items: center;
        color: #fff;
        padding: 6px 12px;
        border-radius: 12px;
        font-size: 12px;
        font-weight: 700;
        white-space: nowrap;
        background: var(--color-alt, #6c5ce7);
        box-shadow: 0 0 0 2px #fff, 0 2px 8px rgba(0,0,0,0.4);
        z-index: 10;
    }
    .pmap-pin--subject::after {
        content: '';
        position: absolute;
        bottom: -7px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 7px solid transparent;
        border-right: 7px solid transparent;
        border-top: 7px solid var(--color-alt, #6c5ce7);
    }
    .pmap-tooltip {
        position: absolute;
        bottom: calc(100% + 10px);
        left: 50%;
        transform: translateX(-50%);
        background: #fff;
        color: var(--color-primary, #2d3436);
        border-radius: 10px;
        padding: 10px 14px;
        font-size: 12px;
        font-weight: 400;
        white-space: normal;
        box-shadow: 0 4px 16px rgba(0,0,0,0.18);
        pointer-events: auto;
        display: none;
        z-index: 1000;
        line-height: 1.7;
        width: 210px;
        text-align: left;
    }
    .pmap-tooltip::after {
        content: '';
        position: absolute;
        bottom: -10px;
        left: 0;
        right: 0;
        height: 10px;
    }
    .pmap-tooltip__address {
        font-weight: 700;
        font-size: 12px;
        margin-bottom: 4px;
        display: block;
    }
    .pmap-tooltip__row {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        font-size: 12px;
        color: var(--color-primary-70, #636e72);
    }
    .pmap-tooltip__price {
        font-weight: 700;
        font-size: 13px;
        color: var(--color-primary, #2d3436);
        margin-top: 2px;
        display: block;
    }
    .pmap-tooltip__link {
        display: block;
        margin-top: 6px;
        font-size: 11px;
        font-weight: 600;
        color: var(--color-alt, #6c5ce7);
        text-decoration: none;
    }
    .pmap-tooltip__link:hover {
        text-decoration: underline;
    }
    .pmap-tooltip__image {
        width: 100%;
        height: 110px;
        object-fit: cover;
        border-radius: 6px;
        display: block;
        margin-bottom: 8px;
    }
    .pmap-tooltip__meta {
        font-size: 11px;
        color: var(--color-primary-70, #636e72);
        margin-bottom: 2px;
        display: block;
    }
    .pmap-pin:hover .pmap-tooltip {
        display: block;
    }
`;

interface MaplibreScrollZoom {
    enable: () => void;
    disable: () => void;
}

interface MaplibreMarker {
    setLngLat: (lngLat: [number, number]) => this;
    addTo: (map: MaplibreMap) => this;
}

interface MaplibreMap {
    addControl: (control: object, position?: string) => void;
    remove: () => void;
}

interface MaplibreGL {
    Map: new (options: object) => MaplibreMap;
    NavigationControl: new () => object;
    Marker: new (options?: { element?: HTMLElement; anchor?: string }) => MaplibreMarker;
}

const JITTER_RADIUS = 0.0002;
const LOCATION_PRECISION = 4;

const BASE_URL = (process.env.REACT_APP_API_URL ?? '').replace(/\/$/, '');
const API_ORIGIN = BASE_URL ? new URL(BASE_URL).origin : '';

const isAppleDevice = typeof navigator !== 'undefined' &&
    (/iPad|iPhone|iPod|Mac/).test(navigator.userAgent);
const ZOOM_MODIFIER_LABEL = isAppleDevice ? 'Cmd' : 'Ctrl';

const applyJitter = (pins: MapPinDto[]): (MapPinDto & { _lat: number; _lng: number })[] => {
    const groups = new Map<string, MapPinDto[]>();

    pins.forEach((pin) => {
        const key = `${pin.latitude?.toFixed(LOCATION_PRECISION)},${pin.longitude?.toFixed(LOCATION_PRECISION)}`;
        const group = groups.get(key) ?? [];

        group.push(pin);
        groups.set(key, group);
    });

    return pins.map((pin) => {
        const key = `${pin.latitude?.toFixed(LOCATION_PRECISION)},${pin.longitude?.toFixed(LOCATION_PRECISION)}`;
        const group = groups.get(key)!;
        const idx = group.indexOf(pin);

        if (group.length === 1) {
            return { ...pin, _lat: pin.latitude!, _lng: pin.longitude! };
        }

        const angle = 2 * Math.PI * idx / group.length;

        return {
            ...pin,
            _lat: pin.latitude! + JITTER_RADIUS * Math.cos(angle),
            _lng: pin.longitude! + JITTER_RADIUS * Math.sin(angle)
        };
    });
};

const MapContainer = styled.div`
    position: relative;
    width: 100%;
    margin-bottom: 24px;
`;

const MapWrapper = styled.div<{ customHeight?: string }>`
    width: 100%;
    height: ${({ customHeight }) => customHeight ?? 'clamp(400px, calc(100vh - 170px), 100vh)'};
    border-radius: 8px;
    overflow: hidden;
    position: relative;
`;

const ScrollHint = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(0, 0, 0, 0.65);
    color: #fff;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    pointer-events: none;
    z-index: 20;
    text-align: center;
    white-space: nowrap;
`;

const MapControls = styled.div`
    position: absolute;
    bottom: 16px;
    left: 16px;
    z-index: 10;
    display: flex;
    gap: 6px;
`;

const MapToggleBtn = styled.button<{ active?: boolean }>`
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 700;
    border-radius: 20px;
    cursor: pointer;
    border: 1.5px solid var(--color-primary);
    background: ${({ active }) => active ? 'var(--color-primary)' : 'rgba(255,255,255,0.92)'};
    color: ${({ active }) => active ? '#fff' : 'var(--color-primary)'};
    transition: background 0.15s, color 0.15s;
    box-shadow: 0 2px 6px rgba(0,0,0,0.18);

    &:hover {
        background: var(--color-primary);
        color: #fff;
    }
`;

export interface PropertyMapProps {
    mapPins: MapPinDto[];
    tableOffers?: MarketOfferTableRowDto[];
    customHeight?: string;
}

const PropertyMap: FC<PropertyMapProps> = ({ mapPins, tableOffers, customHeight }) => {
    const { user } = useUser();
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<MaplibreMap | null>(null);
    const scrollZoomRef = useRef<MaplibreScrollZoom | null>(null);
    const hintTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [is3d, setIs3d] = useState(false);
    const [scrollHintText, setScrollHintText] = useState<string | null>(null);

    const POLAND_CENTER_LNG = 19.0;
    const POLAND_CENTER_LAT = 52.0;

    const subject = mapPins.find((p) => p.isSubjectProperty);
    const centerLng = subject?.longitude ?? POLAND_CENTER_LNG;
    const centerLat = subject?.latitude ?? POLAND_CENTER_LAT;

    const showHint = (text: string) => {
        setScrollHintText(text);
        if (hintTimeoutRef.current) clearTimeout(hintTimeoutRef.current);
        hintTimeoutRef.current = setTimeout(() => setScrollHintText(null), 2000);
    };

    useEffect(() => {
        const maplibregl = (window as Window & { maplibregl?: MaplibreGL }).maplibregl;

        if (!maplibregl || !mapContainerRef.current) return;

        if (mapRef.current) {
            mapRef.current.remove();
            mapRef.current = null;
        }

        const map = new maplibregl.Map({
            container: mapContainerRef.current,
            style: `${BASE_URL}/map/styles/custom?theme=BASIC&enable3d=${is3d}`,
            center: [centerLng, centerLat],
            zoom: 14,
            pitch: is3d ? 60 : 0,
            bearing: is3d ? -20 : 0,
            preserveDrawingBuffer: true,
            scrollZoom: false,
            transformRequest: (url: string) => {
                let finalUrl = url.startsWith('/') ? BASE_URL + url : url;

                try {
                    const parsed = new URL(finalUrl);

                    if (parsed.origin !== API_ORIGIN && parsed.pathname.startsWith('/map/')) {
                        finalUrl = API_ORIGIN + parsed.pathname + parsed.search;
                    }
                } catch {
                    // Parsowanie URL może się nie udać dla ścieżek względnych, można je zostawić bez zmian
                }

                if (finalUrl.startsWith(BASE_URL)) {
                    return { url: finalUrl, headers: { Authorization: 'Bearer ' + user.token } };
                }

                return { url: finalUrl };
            }
        });

        scrollZoomRef.current = (map as unknown as { scrollZoom: MaplibreScrollZoom }).scrollZoom;

        map.addControl(new maplibregl.NavigationControl(), 'top-right');

        const offerLookup = new Map<number, MarketOfferTableRowDto>();
        tableOffers?.forEach((row) => {
            if (row.advertisementId != null) offerLookup.set(row.advertisementId, row);
        });

        const sortedPins = [...mapPins].sort((a, b) => (a.isSubjectProperty ? 1 : 0) - (b.isSubjectProperty ? 1 : 0));
        const pinsWithCoords = applyJitter(sortedPins);

        const labelPinIds = new Set(
            pinsWithCoords
                .filter((p) => !p.isSubjectProperty)
                .sort((a, b) => {
                    const priceA = a.area && a.totalPrice != null ? a.totalPrice / a.area : Infinity;
                    const priceB = b.area && b.totalPrice != null ? b.totalPrice / b.area : Infinity;

                    return priceA - priceB;
                })
                .slice(0, PIN_LABEL_COUNT)
                .map((p) => p.advertisementId)
        );

        pinsWithCoords.forEach((pin) => {
            const isLabelPin = pin.isSubjectProperty || labelPinIds.has(pin.advertisementId);
            const el = document.createElement('div');

            if (pin.isSubjectProperty) {
                el.className = 'pmap-pin pmap-pin--subject';
                el.textContent = pin.displayPricePerSqm ?? 'WYCENA';
            } else if (isLabelPin) {
                el.className = 'pmap-pin pmap-pin--label';
                el.textContent = pin.totalPrice != null
                    ? `${Math.round(pin.totalPrice).toLocaleString('pl-PL')} zł`
                    : pin.displayPricePerSqm ?? '';
            } else {
                el.className = 'pmap-pin pmap-pin--dot';
                el.innerHTML = '<svg width="24" height="26" viewBox="-1.5 -1.5 21 23" xmlns="http://www.w3.org/2000/svg"><path d="M0.0211454 8.41656C-0.345912 3.5831 4.12259 0 9 0C13.8774 0 18.3459 3.5831 17.9789 8.41656C17.9349 8.99549 17.8664 9.54275 17.7617 10.0103C17.2534 12.2803 15.7089 14.3951 14.1464 16.0594C12.5712 17.7373 10.9062 19.0319 10.0474 19.6592C9.42544 20.1136 8.57455 20.1136 7.95258 19.6592C7.09382 19.0319 5.42877 17.7373 3.85358 16.0594C2.29114 14.3951 0.74659 12.2803 0.238281 10.0103C0.133594 9.54275 0.0651105 8.99549 0.0211454 8.41656Z" fill="#2d3436" stroke="#fff" stroke-width="2" paint-order="stroke fill"/></svg>';
            }

            const offerRow = offerLookup.get(pin.advertisementId);

            const pricePerSqm = offerRow?.pricePerSqm != null
                ? Math.round(offerRow.pricePerSqm).toLocaleString('pl-PL') + ' zł/m²'
                : pin.area && pin.totalPrice != null
                    ? Math.round(pin.totalPrice / pin.area).toLocaleString('pl-PL') + ' zł/m²'
                    : pin.displayPricePerSqm ?? null;

            const tooltipParts: string[] = [];

            if (offerRow?.mainImage) {
                const thumbUrl = convertToThumbnailPath(offerRow.mainImage, 'SALE_FLAT');

                tooltipParts.push(`<img class="pmap-tooltip__image" src="${thumbUrl}" alt="" loading="lazy" />`);
            }

            if (pin.address) {
                tooltipParts.push(`<span class="pmap-tooltip__address">${pin.address}</span>`);
            }

            const metaParts: string[] = [];
            if (offerRow?.numberOfRooms != null) metaParts.push(`${offerRow.numberOfRooms} pok.`);
            if (offerRow?.floor) metaParts.push(`piętro ${offerRow.floor}`);

            if (metaParts.length > 0) {
                tooltipParts.push(`<span class="pmap-tooltip__meta">${metaParts.join(' · ')}</span>`);
            }

            if (pin.area != null || pricePerSqm) {
                const left = pin.area != null ? `${pin.area} m²` : '';
                const right = pricePerSqm ?? '';

                tooltipParts.push(`<span class="pmap-tooltip__row"><span>${left}</span><span>${right}</span></span>`);
            }

            if (pin.totalPrice != null) {
                tooltipParts.push(`<span class="pmap-tooltip__price">${Math.round(pin.totalPrice).toLocaleString('pl-PL')} zł</span>`);
            }

            if (!pin.isSubjectProperty && pin.encryptedId) {
                const offerUrl = `${window.location.origin}/sale/flat/${pin.encryptedId}`;

                tooltipParts.push(`<a class="pmap-tooltip__link" href="${offerUrl}" target="_blank" rel="noopener noreferrer">→ Zobacz ogłoszenie</a>`);
            }

            const tooltip = document.createElement('div');

            tooltip.className = 'pmap-tooltip';
            tooltip.innerHTML = tooltipParts.join('');
            el.appendChild(tooltip);

            el.addEventListener('mouseenter', () => {
                if (el.parentElement) el.parentElement.style.zIndex = '10000';
            });
            el.addEventListener('mouseleave', () => {
                if (el.parentElement) el.parentElement.style.zIndex = '';
            });

            new maplibregl.Marker({ element: el, anchor: 'bottom' })
                .setLngLat([pin._lng, pin._lat])
                .addTo(map);
        });

        const containerEl = mapContainerRef.current;

        const wheelHandler = (e: WheelEvent) => {
            if (e.ctrlKey || e.metaKey) {
                scrollZoomRef.current?.enable();
            } else {
                scrollZoomRef.current?.disable();
                showHint(`Przytrzymaj ${ZOOM_MODIFIER_LABEL}, aby powiększyć mapę`);
            }
        };

        containerEl?.addEventListener('wheel', wheelHandler, { passive: true });

        mapRef.current = map;

        return () => {
            containerEl?.removeEventListener('wheel', wheelHandler);
            if (hintTimeoutRef.current) clearTimeout(hintTimeoutRef.current);

            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, [
        centerLng,
        centerLat,
        user.token,
        mapPins,
        tableOffers,
        is3d
    ]);

    return (
        <MapContainer>
            <PinStyles />
            <MapWrapper ref={mapContainerRef} customHeight={customHeight} data-map-capture="true" />
            {scrollHintText && <ScrollHint>{scrollHintText}</ScrollHint>}
            <MapControls>
                <MapToggleBtn active={!is3d} onClick={() => setIs3d(false)}>2D</MapToggleBtn>
                <MapToggleBtn active={is3d} onClick={() => setIs3d(true)}>3D</MapToggleBtn>
            </MapControls>
        </MapContainer>
    );
};

export default PropertyMap;
